import getParent from "@/utils/getParent";
import { useMemoizedFn } from "ahooks";
import classNames from "classnames";
import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import styles from "./index.module.less";

interface IProps {
  onDragSortEnd: (data: { draggedItem: any; position: number }) => void;
  dragEleScale?: number;
  dragPreviewClass?: string;
}

export function useDraggable({
  onDragSortEnd,
  dragEleScale = 0.54,
  dragPreviewClass = "",
}: IProps) {
  // 新增：拖拽相关状态
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  /**拖拽元素位置 */
  const dragEleCopyRef = useRef(null);
  const dropIndicatorRef = useRef(null);
  const dragStartPos = useRef({ x: 0, y: 0, position: null });
  const dropPositionIndexRef = useRef(null);

  const scroll = useDragOverScroll();

  // 新增：处理拖拽移动
  const handleDrag = useMemoizedFn((e: MouseEvent) => {
    e.stopPropagation();
    if (!dragEleCopyRef.current) return;

    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;

    if (!(Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3)) {
      return;
    }

    setIsDragging(true);

    dragEleCopyRef.current.style.display = "block";

    dragEleCopyRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(${dragEleScale})`;

    // 查找最近的目标元素并更新占位符
    const target = document.elementFromPoint(e.clientX, e.clientY);

    const brickDom = getParent(target, "[data-mark]");

    // 设置占位图的位置
    if (brickDom && dropIndicatorRef.current) {
      const dropIndicator = dropIndicatorRef.current;
      const rect = brickDom.getBoundingClientRect();

      // 在前面
      const isBefore = e.clientX - rect.left < rect.width / 2;

      dropIndicator.style.left = isBefore
        ? `${rect.left - 8}px`
        : `${rect.left + rect.width + 2}px`;
      dropIndicator.style.top = `${rect.top + 2}px`;
      dropIndicator.style.height = `${rect.height - 4}px`;
      dropIndicator.style.display = "block";

      const targetId = Number(brickDom.getAttribute("data-mark"));

      dropPositionIndexRef.current = {
        targetId,
        isBefore,
      };
    }

    // 处理自动滚动
    const viewportHeight = window.innerHeight;
    const mousePositionY = e.clientY;
    const scrollThreshold = 20; // 滚动触发阈值

    if (mousePositionY < scrollThreshold) {
      // 靠近顶部，向上滚动
      const dropIndicator = dropIndicatorRef.current;
      dropIndicator.style.display = "none";
      scroll.startScroll(-5); // 负值表示向上滚动
    } else if (mousePositionY > viewportHeight - scrollThreshold) {
      // 靠近底部，向下滚动
      const dropIndicator = dropIndicatorRef.current;
      dropIndicator.style.display = "none";
      scroll.startScroll(5);
    } else {
      scroll.stopScroll();
    }
  });

  // 新增：处理拖拽结束
  const handleDragEnd = useMemoizedFn(() => {
    if (draggedItem && dropPositionIndexRef.current !== null) {
      onDragSortEnd?.({
        draggedItem,
        position: dropPositionIndexRef.current,
      });
    }

    if (dragEleCopyRef.current) {
      dragEleCopyRef.current.remove();
      dragEleCopyRef.current = null;
    }

    if (dropIndicatorRef.current) {
      dropIndicatorRef.current.remove();
      dropPositionIndexRef.current = null;
    }

    setIsDragging(false);
    setDraggedItem(null);
    dropPositionIndexRef.current = null;
    scroll.stopScroll();

    window.removeEventListener("mousemove", handleDrag);
    window.removeEventListener("mouseup", handleDragEnd);
  });

  // 新增：处理拖拽开始
  const handleMouseDown = useMemoizedFn(
    async (e: ReactMouseEvent<HTMLDivElement>, item) => {
      if (e.button !== 0) {
        return;
      }

      const brick = e.currentTarget;

      // 创建拖拽预览元素
      const copyEle = brick.cloneNode(true) as HTMLDivElement;
      copyEle.className = classNames(styles.dragCopyEle, dragPreviewClass);
      // 更新预览元素位置
      copyEle.style.left = `${e.clientX - 15}px`;
      copyEle.style.top = `${e.clientY - 15}px`;
      copyEle.style.transform = `translate(0px, 0px) scale(${dragEleScale})`;
      copyEle.style.display = "none";

      document.body.appendChild(copyEle);
      dragEleCopyRef.current = copyEle;

      const dropIndicator = document.createElement("div");
      dropIndicatorRef.current = dropIndicator;
      dropIndicator.className = styles.dropIndicator;
      document.body.appendChild(dropIndicator);
      dropIndicator.style.display = "none";

      setDraggedItem(item);
      dragStartPos.current = {
        x: e.clientX,
        y: e.clientY,
        position: Number(brick.getAttribute("data-mark")),
      };

      window.addEventListener("mousemove", handleDrag);
      window.addEventListener("mouseup", handleDragEnd);
    }
  );

  return {
    isDragging,
    handleDragStart: handleMouseDown,
  };
}

function useDragOverScroll() {
  const scrollSpeedRef = useRef(0);
  const scrollTargetRef = useRef(null);
  const frameRef = useRef(null);

  const scroll = useMemoizedFn(() => {
    if (scrollSpeedRef.current === 0) {
      return;
    }

    const currentScroll = window.scrollY;
    window.scrollTo({
      top: currentScroll + scrollSpeedRef.current,
    });

    frameRef.current = requestAnimationFrame(scroll);
  });

  const startScroll = useMemoizedFn((speed = 5) => {
    scrollSpeedRef.current = speed;
    if (!frameRef.current) {
      frameRef.current = requestAnimationFrame(scroll);
    }
  });

  const stopScroll = useMemoizedFn(() => {
    scrollSpeedRef.current = 0;
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  });

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  return {
    startScroll,
    stopScroll,
    scrollSpeedRef,
    scrollTargetRef,
  };
}
